import { alpha, Box, ButtonBase, Container, Typography } from '@mui/material';
import LaboratoryPicker from 'components/LaboratoryPicker';
import Logo from 'components/LogoSign';
import useAuth from 'hooks/useAuth';
import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

/**
 * Shown right after login, only for users linked to more than one laboratory
 * who have not picked one yet. After the first choice the last laboratory is
 * used automatically on every login.
 */
const LaboratorySelection: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user, laboratories, selectLaboratory, logout } = useAuth();
  const [selectingId, setSelectingId] = useState<string | null>(null);

  const firstName = (user?.fullName ?? '').trim().split(/\s+/)[0];

  const handleSelect = async (laboratoryId: string): Promise<void> => {
    setSelectingId(laboratoryId);
    try {
      await selectLaboratory(laboratoryId);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t('Could not select the laboratory'), {
        variant: 'error',
      });
      setSelectingId(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('Select a laboratory')}</title>
      </Helmet>
      <Box
        sx={(theme) => ({
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          py: { xs: 4, md: 8 },
          background: `radial-gradient(circle at 15% 0%, ${alpha(
            theme.palette.primary.main,
            0.14,
          )} 0%, transparent 45%), radial-gradient(circle at 90% 100%, ${alpha(
            theme.palette.primary.main,
            0.1,
          )} 0%, transparent 40%), ${theme.palette.background.default}`,
        })}
      >
        <Container maxWidth="md">
          <Logo />

          <Box sx={{ textAlign: 'center', mt: 4, mb: 4 }}>
            <Typography variant="h2" sx={{ mb: 1 }}>
              {firstName
                ? `${t('Hello')}, ${firstName}!`
                : t('Select a laboratory')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: 'text.secondary',
                fontWeight: 'normal',
                maxWidth: 520,
                mx: 'auto',
              }}
            >
              {t(
                'Which laboratory do you want to work on? You can switch later from the user menu.',
              )}
            </Typography>
          </Box>

          <LaboratoryPicker
            laboratories={laboratories}
            selectingId={selectingId}
            onSelect={handleSelect}
          />

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <ButtonBase
              disabled={Boolean(selectingId)}
              onClick={() => logout()}
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                '&:hover': { color: 'primary.main' },
              }}
            >
              {t('Sign in with another account')}
            </ButtonBase>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LaboratorySelection;
